import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  Block,
  ContactSupportOutlined,
  Logout,
  WarningAmberOutlined,
  WorkOutline,
} from "@mui/icons-material";

import StatusChip from "app/common/StatusChip";
import { getUser } from "app/store/modules/selectors/account.selector";

const unavailableReasonId = (workspace) => {
  if (workspace.disabled) return "WORKSPACE_ACCESS.REASON.MEMBERSHIP_DISABLED";
  if (workspace.workspaceDisabled) return "WORKSPACE_ACCESS.REASON.WORKSPACE_SUSPENDED";
  return "WORKSPACE_ACCESS.REASON.UNAVAILABLE";
};

const workspaceInitial = (name) => name?.trim().charAt(0).toUpperCase() || "W";

const userDisplayName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "User";

const userInitials = (user) =>
  [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
  user?.email?.charAt(0).toUpperCase() ||
  "U";

export default function NoWorkspaceAccess({ workspaces = [] }) {
  const user = useSelector(getUser);
  const unavailable = workspaces.filter(
    (workspace) => workspace.disabled || workspace.workspaceDisabled
  );

  return (
    <main className="container py-10">
      <div className="workspace-access-shell">
        <section className="bg-white rounded p-30px" aria-labelledby="workspace-access-title">
          <div className="workspace-access-notice">
            <div className="workspace-access-notice-icon" aria-hidden="true">
              <WarningAmberOutlined />
            </div>
            <div>
              <div className="f-20px text-dark font-weight-medium" id="workspace-access-title">
                <FormattedMessage id="WORKSPACE_ACCESS.TITLE" />
              </div>
              <p className="mb-0 mt-5px f-14px text-dark fw-300">
                <FormattedMessage id="WORKSPACE_ACCESS.DESCRIPTION" />
              </p>
              <p className="mb-0 mt-10px f-14px text-dark fw-300">
                <FormattedMessage id="WORKSPACE_ACCESS.NEXT_STEP.DESCRIPTION" />
                <a
                  href="mailto:support@kaddim.com"
                  className="kt-link workspace-access-support-link"
                >
                  <ContactSupportOutlined fontSize="inherit" className="mr-5px" />
                  <FormattedMessage id="WORKSPACE_ACCESS.CONTACT_SUPPORT" />
                </a>
              </p>
              {user && (
                <div className="d-flex align-items-center mt-15px">
                  <div className="workspace-access-monogram" aria-hidden="true">
                    {userInitials(user)}
                  </div>
                  <div>
                    <div className="f-12px text-muted">
                      <FormattedMessage id="WORKSPACE_ACCESS.SIGNED_IN_AS" />
                    </div>
                    <div className="f-14px text-dark font-weight-medium">
                      {userDisplayName(user)}
                    </div>
                    {user.email && <div className="f-12px text-muted">{user.email}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-20px text-center">
            <Link to="/logout" className="btn btn-outline-danger btn-elevate py-3">
              <Logout fontSize="small" className="mr-5px" aria-hidden="true" />
              <FormattedMessage id="USER_MENU.LOGOUT" />
            </Link>
          </div>

          {unavailable.length > 0 && (
            <div className="mt-30px" aria-labelledby="workspace-access-list-title">
              <div className="d-flex align-items-center mb-15px">
                <WorkOutline aria-hidden="true" />
                <h2
                  id="workspace-access-list-title"
                  className="f-16px text-dark font-weight-medium mb-0 ml-10px"
                >
                  <FormattedMessage id="WORKSPACE_ACCESS.LIST.TITLE" />
                </h2>
              </div>
              <div className="row">
                {unavailable.map((workspace) => (
                  <div className="col-md-6 col-12 mb-15px" key={workspace.workspaceId}>
                    <article className="workspace-access-workspace-card">
                      <div className="workspace-access-monogram" aria-hidden="true">
                        {workspaceInitial(workspace.name)}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <h3 className="f-16px text-dark font-weight-medium mb-5px">
                          {workspace.name}
                        </h3>
                        <p className="f-14px text-dark fw-300 mb-0">
                          <FormattedMessage id={unavailableReasonId(workspace)} />
                        </p>
                      </div>
                      <span className="ml-10px">
                        <StatusChip icon={<Block fontSize="small" />} tone="warning">
                          <FormattedMessage id="WORKSPACE_ACCESS.STATUS.UNAVAILABLE" />
                        </StatusChip>
                      </span>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
